// Bundle picker on the single product page (markup: render_bundle_picker() in
// includes/functions/bundles.php). Keeps the customer oriented at every step:
// the add button says exactly what's still missing ("Choose 1 more flavour",
// "Pick 2 more"), picks show up as removable chips, the saving updates with
// the real picks, and adding goes straight into the cart drawer (AJAX) — with
// a normal form submit as the fallback. The server re-validates everything.

import { openWithFragments } from './cart-drawer';

function ajaxUrl() {
    return (window.settings && window.settings.admin_ajax_url) || '/wp-admin/admin-ajax.php';
}

function money(amount, symbol) {
    return symbol + (Math.round(amount * 100) / 100).toFixed(2);
}

function initBundle(root) {
    const form = root.closest('form.cart');
    const button = form && form.querySelector('.single_add_to_cart_button');
    if (!form || !button) return;

    const isMix = root.dataset.type === 'mix';
    const bundlePrice = parseFloat(root.dataset.price) || 0;
    const symbol = root.dataset.currency || '£';
    const hint = root.querySelector('[data-fc-bundle-hint]');
    const worthEl = root.querySelector('[data-fc-bundle-worth]');
    const saveEl = root.querySelector('[data-fc-bundle-save]');
    const originalLabel = button.textContent.trim();
    let missingMessage = '';

    function setHint(text, tone) {
        if (!hint) return;
        hint.textContent = text;
        hint.className = 'fc-bundle__hint' + (tone ? ` fc-bundle__hint--${tone}` : '');
    }

    function setSaving(worth) {
        if (!worthEl || !saveEl || !worth) return;
        worthEl.textContent = money(worth, symbol);
        saveEl.textContent = money(Math.max(0, worth - bundlePrice), symbol);
    }

    // Never disabled: a dead button tells the customer nothing — clicking an
    // incomplete one points at what's missing instead (see submit below).
    function setButton(missingLabel) {
        button.classList.toggle('fc-bundle-incomplete', !!missingLabel);
        button.setAttribute('aria-disabled', missingLabel ? 'true' : 'false');
        button.textContent = missingLabel || originalLabel;
    }

    // "Can't do that" moments get an explanation and a little shake instead
    // of a silently dead click.
    function flag(text) {
        setHint(text, 'error');
        root.classList.remove('fc-bundle--nudge');
        void root.offsetWidth; // restart the nudge animation
        root.classList.add('fc-bundle--nudge');
    }

    // ---- Fixed bundle: one flavour per variable item -----------------------
    function updateFixed() {
        const missing = [];
        let worth = 0;
        root.querySelectorAll('[data-fc-item]').forEach((item) => {
            const qty = parseInt(item.dataset.qty, 10) || 1;
            const checked = item.querySelector('input[type="radio"]:checked');
            const needsChoice = item.dataset.choose === '1';
            item.classList.toggle('is-complete', !needsChoice || !!checked);
            worth += qty * (checked ? parseFloat(checked.dataset.price) : parseFloat(item.dataset.basePrice)) || 0;
            if (needsChoice && !checked) missing.push(item.dataset.name);
        });
        setSaving(worth);
        if (missing.length) {
            missingMessage = `Choose a flavour for ${missing.join(' and ')}.`;
            setButton(missing.length === 1 ? 'Choose 1 more flavour' : `Choose ${missing.length} more flavours`);
            setHint('');
        } else {
            missingMessage = '';
            setButton('');
            setHint('All set — your bundle is ready.', 'ok');
        }
    }

    function bindFixed() {
        root.querySelectorAll('[data-fc-item]').forEach((item) => {
            const thumb = item.querySelector('[data-fc-item-thumb]');
            item.querySelectorAll('input[type="radio"]').forEach((radio) => {
                radio.addEventListener('change', () => {
                    // Show the chosen flavour's own picture when it has one.
                    if (thumb && radio.dataset.image) thumb.src = radio.dataset.image;
                    updateFixed();
                });
            });
        });
        updateFixed();
    }

    // ---- Pick & mix: N items from the grid ---------------------------------
    const size = parseInt(root.dataset.size, 10) || 0;
    const maxEach = parseInt(root.dataset.maxEach, 10) || 0;
    const options = Array.from(root.querySelectorAll('[data-fc-option]'));
    const picksEl = root.querySelector('[data-fc-bundle-picks]');
    const counter = root.querySelector('[data-fc-bundle-counter]');
    const bar = root.querySelector('[data-fc-bundle-bar]');

    const qtyOf = (option) => parseInt(option.querySelector('[data-fc-qty]').value, 10) || 0;
    const total = () => options.reduce((sum, o) => sum + qtyOf(o), 0);
    const nameOf = (option) => option.querySelector('.fc-bundle__option-name').textContent.trim();

    const stockOf = (option) => (option.dataset.stock === '' ? Infinity : parseInt(option.dataset.stock, 10));

    // Why this flavour can't take one more (stock / per-flavour cap), if so.
    function limitOf(option) {
        const qty = qtyOf(option);
        if (qty >= stockOf(option)) return `Only ${stockOf(option)} ${nameOf(option)} left — try another flavour.`;
        if (maxEach && qty >= maxEach) return `Max ${maxEach} of each flavour — try another one.`;
        return '';
    }

    function change(option, delta) {
        if (delta > 0) {
            if (option.classList.contains('is-disabled')) return;
            const limit = limitOf(option);
            if (limit) return flag(limit);
            if (total() >= size) return flag(`Your box is full — tap × on a pick below to swap it for ${nameOf(option)}.`);
        }
        const input = option.querySelector('[data-fc-qty]');
        input.value = Math.max(0, qtyOf(option) + delta);
        updateMix();
    }

    function updateMix() {
        const picked = total();
        const remaining = size - picked;
        let worth = 0;

        options.forEach((option) => {
            const qty = qtyOf(option);
            worth += qty * (parseFloat(option.dataset.price) || 0);
            option.classList.toggle('is-selected', qty > 0);
            option.querySelector('[data-fc-badge]').textContent = qty ? `×${qty}` : '';
            option.querySelector('[data-fc-dec]').disabled = qty === 0;
            // + stays clickable while the box is full: the click explains how
            // to swap instead of doing nothing. Only hard limits grey it out.
            option.querySelector('[data-fc-inc]').disabled = option.classList.contains('is-disabled') || !!limitOf(option);
        });

        if (counter) counter.textContent = `${picked} of ${size} picked`;
        if (bar) bar.style.width = `${size ? (picked / size) * 100 : 0}%`;
        root.classList.toggle('is-full', remaining === 0);

        // Removable chips of what's been picked so far.
        if (picksEl) {
            picksEl.innerHTML = '';
            options.filter((o) => qtyOf(o) > 0).forEach((option) => {
                const chip = document.createElement('button');
                chip.type = 'button';
                chip.className = 'fc-bundle__pick';
                chip.innerHTML = `<span>${qtyOf(option)}× ${nameOf(option)}</span><span aria-hidden="true">×</span>`;
                chip.setAttribute('aria-label', `Remove one ${nameOf(option)}`);
                chip.addEventListener('click', () => change(option, -1));
                picksEl.appendChild(chip);
            });
            picksEl.hidden = picked === 0;
        }

        if (picked === size) setSaving(worth);
        if (remaining > 0) {
            missingMessage = `Pick ${remaining} more to complete your bundle.`;
            setButton(`Pick ${remaining} more`);
            setHint(picked === 0 ? `Tap a flavour to add it — any ${size}, repeats welcome.` : `${remaining} to go — keep picking.`);
        } else {
            missingMessage = '';
            setButton('');
            setHint('Bundle complete — nice picks!', 'ok');
        }
    }

    // The "x of N picked" bar sticks under the header (CSS); flag when it's
    // actually floating over the grid so it can get its shadow.
    const status = root.querySelector('.fc-bundle__status');
    function markStuck() {
        const line = parseFloat(getComputedStyle(status).top) || 0;
        status.classList.toggle('is-stuck', status.parentElement.getBoundingClientRect().top < line && status.getBoundingClientRect().top <= line + 1);
    }

    function bindMix() {
        if (status) {
            let queued = false;
            window.addEventListener('scroll', () => {
                if (queued) return;
                queued = true;
                requestAnimationFrame(() => { queued = false; markStuck(); });
            }, { passive: true });
            markStuck();
        }
        options.forEach((option) => {
            option.querySelector('[data-fc-inc]').addEventListener('click', () => change(option, 1));
            option.querySelector('[data-fc-dec]').addEventListener('click', () => change(option, -1));
            const card = option.querySelector('[data-fc-add]');
            if (card) card.addEventListener('click', () => change(option, 1));
        });
        updateMix();
    }

    // ---- Submit: straight into the cart drawer -----------------------------
    form.addEventListener('submit', (event) => {
        if (missingMessage) {
            event.preventDefault();
            flag(missingMessage);
            return;
        }
        if (!window.fetch || !window.FormData) return; // plain form post fallback

        event.preventDefault();
        const body = new FormData(form);
        body.append('action', 'fc_add_product_bundle');
        body.append('nonce', (window.settings && window.settings.cart_nonce) || '');
        body.append('product_id', button.value);
        button.disabled = true;
        button.classList.add('is-loading');

        fetch(ajaxUrl(), { method: 'POST', body, credentials: 'same-origin' })
            .then((response) => response.json())
            .then((result) => {
                button.classList.remove('is-loading');
                button.disabled = false;
                if (result && result.success) {
                    if (isMix) {
                        // Box is in the basket: start a fresh one for "another box?".
                        options.forEach((option) => { option.querySelector('[data-fc-qty]').value = 0; });
                        updateMix();
                        setHint(`Added to your basket — fancy another box? Pick any ${size}.`, 'ok');
                    } else {
                        setHint('Added to your basket.', 'ok');
                    }
                    openWithFragments(result.data.fragments);
                } else {
                    setHint((result && result.data && result.data.message) || 'Sorry, something went wrong — please try again.', 'error');
                }
            })
            .catch(() => form.submit());
    });

    if (isMix) bindMix(); else bindFixed();
}

function init() {
    document.querySelectorAll('[data-fc-bundle]').forEach(initBundle);
}

export default { init };
